import React, { useEffect, useMemo, useState } from "react";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import { Check, X } from "lucide-react";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import NewCampaignModal from "@/features/campaigns/components/UserCampaigns/components/NewCampaignModal";

// z-index for the modal to be above the drawer
const DRAWER_ABOVE_OVERLAY_CLASSES = "!z-[999999999]";

const CampaignChoiceModal = ({
  isOpen,
  campaigns,
  onSelectCampaign,
  onClose,
  isBusy,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  useEffect(() => {
    if (!isOpen) setSearchValue("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !isBusy) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, isBusy, onClose]);

  const term = searchValue.trim().toLowerCase();
  const filteredCampaigns = useMemo(() => {
    if (!term) return campaigns;
    return campaigns.filter((c) => (c.name || "").toLowerCase().includes(term));
  }, [campaigns, term]);

  const handleSafeClose = () => {
    if (!isBusy) onClose?.();
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        width="500px"
        overlayClassName={DRAWER_ABOVE_OVERLAY_CLASSES}
        className="!p-6 !flex !flex-col !gap-4 !items-stretch max-h-[80dvh]"
      >
        <div className="flex w-full flex-col gap-4 min-h-0">
          <div className="flex justify-between items-start gap-3 shrink-0">
            <div className="flex flex-col gap-0.5 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900">
                {campaigns.length > 0
                  ? "Choose a campaign"
                  : "No campaigns available"}
              </h2>
              {campaigns.length > 0 && (
                <p className="text-sm text-gray-500">
                  Select one campaign. Enrichment and import will start
                  immediately.
                </p>
              )}
            </div>
            <button
              type="button"
              className="p-1 hover:bg-gray-100 rounded-full shrink-0 disabled:opacity-50"
              onClick={handleSafeClose}
              disabled={isBusy}
              aria-label="Close"
            >
              <X size={18} color="#181D27" />
            </button>
          </div>

          {campaigns.length > 0 && (
            <div className="shrink-0 w-full px-1">
              <SearchBar
                value={searchValue}
                setFilterValue={setSearchValue}
                onChange={setSearchValue}
                placeholder="Search campaigns..."
                className="w-full"
                disabled={isBusy}
                debounceDelay={0}
              />
            </div>
          )}

          <div className="max-h-[min(360px,calc(80dvh-12rem))] overflow-y-auto pr-1">
            {campaigns.length === 0 ? (
              <div className="flex flex-col">
                <p className="py-8 text-center text-sm text-gray-500">
                  No campaigns are available to assign.
                </p>
                <PrimaryButton
                  className="w-fit mx-auto"
                  onClick={() => {
                    setShowCreateCampaignModal(true);
                    handleSafeClose();
                  }}
                >
                  Create Campaign
                </PrimaryButton>
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                No campaigns match &quot;{searchValue}&quot;.
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {filteredCampaigns.map((campaign) => (
                  <li key={campaign.id}>
                    <button
                      type="button"
                      onClick={() => onSelectCampaign(campaign.id)}
                      disabled={isBusy}
                      className="group flex w-full items-center justify-between gap-3 rounded-lg border border-transparent px-3 py-2.5 text-left text-sm font-medium text-gray-800 hover:border-secondary/30 hover:bg-secondary/5 hover:text-secondary disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span className="min-w-0 truncate">{campaign.name}</span>
                      <Check
                        size={16}
                        className="shrink-0 text-secondary opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex shrink-0 justify-end border-t border-gray-100 pt-4">
            <SecondaryButton
              title="Cancel"
              size="sm"
              onClick={handleSafeClose}
              disabled={isBusy}
            />
          </div>
        </div>
      </CustomModal>

      {showCreateCampaignModal && (
        <NewCampaignModal
          isOpen={showCreateCampaignModal}
          onClose={(finalId) => {
            setShowCreateCampaignModal(false);
            if (typeof finalId === "string" && finalId) {
              onSelectCampaign(finalId);
            }
          }}
          width="1000px"
        />
      )}
    </>
  );
};

export default CampaignChoiceModal;
